import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  FileText, 
  Phone, 
  ClipboardList, 
  HeartPulse,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

export const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      icon: FileText,
      title: 'Verordnung',
      description: 'Sie erhalten eine Verordnung für Logopädie von Ihrem Arzt (Hausarzt, HNO, Kinderarzt, Neurologe etc.).',
    },
    {
      number: '02',
      icon: Phone,
      title: 'Terminvereinbarung',
      description: 'Rufen Sie mich an oder schreiben Sie eine E-Mail. Wir vereinbaren zeitnah einen Ersttermin.',
    },
    {
      number: '03',
      icon: ClipboardList,
      title: 'Erstgespräch & Diagnostik',
      description: 'Im ersten Termin lernen ich Sie kennen, führe eine Diagnostik durch und erstellen einen individuellen Therapieplan.',
    },
    {
      number: '04',
      icon: HeartPulse,
      title: 'Therapie',
      description: 'Die Therapie findet regelmäßig statt. Wir arbeiten gemeinsam an Ihren Zielen und passen die Behandlung bei Bedarf an.',
    },
  ];

  const faqs = [
    {
      question: 'Brauche ich eine Überweisung?',
      answer: 'Ja, für die Therapie benötigen Sie eine Verordnung vom Arzt. Diese kann von Hausärzten, HNO-Ärzten, Kinderärzten, Neurologen oder anderen Fachärzten ausgestellt werden.',
    },
    {
      question: 'Werden die Kosten übernommen?',
      answer: 'Gesetzliche und private Krankenkassen übernehmen in der Regel die Kosten für logopädische Therapie. Bei gesetzlich Versicherten ab 18 Jahren fällt eine Zuzahlung an.',
    },
    {
      question: 'Wie oft findet die Therapie statt?',
      answer: 'In der Regel 1-2 Mal pro Woche, je nach Verordnung und individuellem Bedarf. Die Sitzungsdauer beträgt meist 45 Minuten.',
    },
  ];

  return (
    <section id="ablauf" className="section-padding bg-background relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015]">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="process-pattern" x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="hsl(var(--primary))" />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill="url(#process-pattern)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            So funktioniert&apos;s
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Ablauf & Organisation
          </h2>
          <p className="text-body font-body text-lg leading-relaxed">
            Der Weg zur logopädischen Therapie ist einfach. Hier erfahren Sie, 
            wie Sie einen Termin bei uns bekommen.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative mb-20">
          {/* Connection line - desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/20 to-transparent -translate-y-1/2" />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="relative">
                <Card className="h-full bg-card hover:bg-card-elevated border-border hover:border-primary/20 transition-all duration-300 hover-lift">
                  <CardContent className="p-6 lg:p-8">
                    {/* Step number */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-primary/20 font-heading text-4xl lg:text-5xl font-bold">
                        {step.number}
                      </span>
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <h3 className="font-heading text-xl font-medium text-heading mb-3">
                      {step.title}
                    </h3>
                    <p className="text-body font-body text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Arrow - between cards on desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-4 w-8 h-8 -translate-y-1/2 z-10 items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-primary/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading text-2xl lg:text-3xl font-medium text-heading text-center mb-10">
            Häufige Fragen
          </h3>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-muted/30 border-border hover:border-primary/20 transition-colors duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading text-lg font-medium text-heading mb-2">
                        {faq.question}
                      </h4>
                      <p className="text-body font-body text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Reassurance Box */}
        <div className="mt-12 text-center">
          <Card className="inline-block bg-primary/5 border-primary/10">
            <CardContent className="px-8 py-6">
              <p className="text-body font-body">
                <span className="text-primary font-medium">Keine Sorge:</span>{' '}
                Ich nehme mir Zeit für ein ausführliches Erstgespräch und beantworte 
                all Ihre Fragen persönlich.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
